import {
  Box,
  Button,
  Checkbox,
  Divider,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MdaActiveFilters from "./mda-active-filters";
import MdaApplyFiltersButton from "./mda-apply-filters-button";
import MdaClaimStatusFilter from "./mda-claim-status-filter";
import MdaClientFilter from "./mda-client-filter";
import MdaFilterPlaceholder from "./mda-filter-placeholder";
import MdaIncidentDateFilter from "./mda-incident-date-filter";
import MdaReviewStatusFilter from "./mda-review-status-filter";
import MdaRiskRatingFilter from "./mda-risk-rating-filter";
import { getRiskColor } from "./mda-constants";
import { toTitleCaseRiskRating } from "./mda-helpers";
import {
  ActiveFilter,
  MdaClientOption,
  MdaFilterActions,
  MdaFilterOption,
  MdaFilterValues,
  MdaSelectOption,
  RiskRating,
} from "./mda-types";

interface MdaFiltersPanelProps {
  filterOptions?: MdaFilterOption[];
  onFilterChange?: (filter: MdaFilterOption) => void;
  values: MdaFilterValues;
  clients: MdaClientOption[];
  claimStatusOptions: MdaSelectOption[];
  claimStatusesLoading: boolean;
  riskRatingOptions: RiskRating[];
  reviewStatusOptions: MdaSelectOption[];
  activeFilters: ActiveFilter[];
  canApplyFilters: boolean;
  actions: MdaFilterActions;
}

const MdaFiltersPanel = ({
  filterOptions = [],
  onFilterChange,
  values,
  clients,
  claimStatusOptions,
  claimStatusesLoading,
  riskRatingOptions,
  reviewStatusOptions,
  activeFilters,
  canApplyFilters,
  actions,
}: MdaFiltersPanelProps) => {
  const hasSelectedClient = Boolean(values.client);
  const renderFilter = (filter: MdaFilterOption) => {
    if (filter.shape === 6) {
      return (
        <MdaIncidentDateFilter
          label={filter.label}
          from={filter.range?.from ? dayjs(filter.range.from) : null}
          to={filter.range?.to ? dayjs(filter.range.to) : null}
          onFromChange={(value) =>
            onFilterChange?.({
              ...filter,
              range: {
                from: value?.toISOString() ?? null,
                to: filter.range?.to ?? null,
              },
            })
          }
          onToChange={(value) =>
            onFilterChange?.({
              ...filter,
              range: {
                from: filter.range?.from ?? null,
                to: value?.toISOString() ?? null,
              },
            })
          }
        />
      );
    }

    const items = [...filter.items].sort(
      (first, second) => first.sortOrder - second.sortOrder,
    );
    const selected = items
      .filter((item) => item.selected)
      .map((item) => item.value);
    const multiple = filter.selectionMode === 2;
    const selectAllValue = "__mda_select_all__";
    const updateSelection = (selection: string[]) =>
      onFilterChange?.({
        ...filter,
        items: filter.items.map((item) => ({
          ...item,
          selected: selection.includes(item.value),
        })),
      });

    return (
      <Stack spacing={0.8}>
        <Typography
          variant="caption"
          sx={{ color: "text.primary", fontWeight: 700 }}
        >
          {filter.label}
        </Typography>
        {filter.isDropdown ? (
          <Select
            multiple={multiple}
            value={multiple ? selected : (selected[0] ?? "")}
            size="small"
            displayEmpty
            inputProps={{ "aria-label": filter.label }}
            renderValue={() => `Select a ${filter.label}`}
            onChange={(event) => {
              const selection = event.target.value;
              if (
                Array.isArray(selection) &&
                selection.includes(selectAllValue)
              ) {
                updateSelection(
                  selected.length === items.length
                    ? []
                    : items.map((item) => item.value),
                );
                return;
              }
              if (typeof selection === "string") {
                updateSelection(selection ? [selection] : []);
              } else {
                updateSelection(selection);
              }
            }}
            sx={{ minWidth: 200, maxWidth: "100%" }}
          >
            {multiple ? (
              <MenuItem value={selectAllValue}>
                <Checkbox
                  checked={
                    Boolean(items.length) && selected.length === items.length
                  }
                  indeterminate={
                    selected.length > 0 && selected.length < items.length
                  }
                  size="small"
                />
                <ListItemText primary="All" />
              </MenuItem>
            ) : (
              <MenuItem value="">Any</MenuItem>
            )}
            {items.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {multiple && <Checkbox checked={item.selected} size="small" />}
                <ListItemText primary={item.description} />
              </MenuItem>
            ))}
          </Select>
        ) : (
          <ToggleButtonGroup
            aria-label={filter.label}
            exclusive={!multiple}
            value={multiple ? selected : (selected[0] ?? null)}
            size="small"
            onChange={(_event, selection: string[] | string | null) => {
              if (Array.isArray(selection)) {
                updateSelection(selection);
              } else {
                updateSelection(selection === null ? [] : [selection]);
              }
            }}
            sx={{
              flexWrap: "wrap",
              ...(multiple && {
                gap: 0.75,
                "& .MuiToggleButtonGroup-grouped": {
                  flex: "1 1 auto",
                  m: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "5px",
                  color: "text.secondary",
                  textTransform: "none",
                  px: 1,
                  minHeight: 40,
                  "&.Mui-selected, &.Mui-selected:hover": {
                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                    color: "info.main",
                    fontWeight: 700,
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.5),
                  },
                },
              }),
            }}
          >
            {items.map((item) => {
              const rating =
                filter.key === "RiskRating"
                  ? toTitleCaseRiskRating(item.description || item.value)
                  : null;
              return (
                <ToggleButton key={item.value} value={item.value}>
                  {rating && (
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        width: 8,
                        height: 8,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: getRiskColor(rating),
                        mr: 0.75,
                      }}
                    />
                  )}
                  {item.description}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        )}
        {!filter.isDropdown && multiple && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Select one or more
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #dfe6f0",
        borderRadius: 1.5,
        // The page is a fixed-height flex column, and overflow: hidden lets a
        // flex item shrink below its content; on shorter screens that clipped
        // the active filters row. Keep the panel at full height and let the
        // page scroll instead.
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <FilterAltOutlinedIcon sx={{ color: "#1c2d49", fontSize: 21 }} />
          <Typography sx={{ color: "#1c2d49", fontWeight: 700 }}>
            Filters
          </Typography>
        </Stack>
        <Button
          onClick={actions.onClear}
          startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
          sx={{ color: "#1769ff", fontWeight: 600, textTransform: "none" }}
        >
          Clear all
        </Button>
      </Stack>
      <Divider />

      {/*
        A wrapping row rather than fixed grid columns: with fr columns the
        client search had no width of its own, so on a single row the other
        filters squeezed it until the selected name was cut off. Each filter
        now has a minimum width and moves to the next line when it won't fit.
      */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: { xs: 2, sm: 2.5 },
          px: { xs: 2, sm: 2.5 },
          py: 2.5,
        }}
      >
        <Box sx={{ flex: "1 1 260px", minWidth: 240, maxWidth: { sm: 360 } }}>
          <MdaClientFilter
            clients={clients}
            selectedClient={values.client}
            onChange={actions.onClientChange}
          />
        </Box>

        {!hasSelectedClient && (
          <Box sx={{ flex: "999 1 260px" }}>
            <MdaFilterPlaceholder />
          </Box>
        )}

        {hasSelectedClient && (
          <>
            {!claimStatusesLoading &&
              [...filterOptions]
                .sort(
                  (first, second) =>
                    (first.sortOrder ?? 0) - (second.sortOrder ?? 0),
                )
                .map((filter) => (
                  <Box
                    key={filter.key}
                    sx={{
                      flex: "0 1 auto",
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    {renderFilter(filter)}
                  </Box>
                ))}
            {claimStatusesLoading && (
              <>
                <Box sx={{ flex: "0 0 auto" }}>
                  <MdaRiskRatingFilter
                    values={values.riskRatings}
                    options={riskRatingOptions}
                    onChange={actions.onRiskChange}
                  />
                </Box>
                <Box sx={{ flex: "0 0 auto" }}>
                  <MdaReviewStatusFilter
                    value={values.reviewStatus}
                    options={reviewStatusOptions}
                    onChange={actions.onReviewStatusChange}
                  />
                </Box>
                <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
                  <MdaClaimStatusFilter
                    values={values.claimStatuses}
                    options={claimStatusOptions}
                    loading={claimStatusesLoading}
                    onChange={actions.onClaimStatusChange}
                  />
                </Box>
                <Box sx={{ flex: "0 1 auto", minWidth: 0, maxWidth: "100%" }}>
                  <MdaIncidentDateFilter
                    from={values.incidentDateFrom}
                    to={values.incidentDateTo}
                    onFromChange={actions.onIncidentDateFromChange}
                    onToChange={actions.onIncidentDateToChange}
                  />
                </Box>
              </>
            )}
            {/* The top padding matches the caption above each input, so the
                button lines up with the inputs on whichever row it lands. */}
            <Box sx={{ flex: "0 0 auto", pt: { sm: "23px" } }}>
              <MdaApplyFiltersButton
                disabled={!canApplyFilters}
                onClick={actions.onApply}
              />
            </Box>
          </>
        )}
      </Box>

      <Divider />
      <MdaActiveFilters filters={activeFilters} onRemove={actions.onRemove} />
    </Paper>
  );
};

export default MdaFiltersPanel;
